import { Link, router } from "@inertiajs/react"
import { formatDistanceToNow } from "date-fns"
import {
  ArrowUpDown,
  Download,
  FileStack,
  ImageIcon,
  Lock,
  PenLine,
  Scissors,
  Trash2,
} from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { pricingPath } from "@/routes"

interface Document {
  id: number
  filename: string
  operation_type: string | null
  status: "pending" | "processing" | "completed" | "failed"
  size: number | null
  created_at: string
  expires_at: string | null
  expired: boolean
  download_url: string | null
}

interface DocumentTableProps {
  documents: Document[]
  plan: "free" | "plus" | "pro"
}

const operationIcons: Record<string, typeof FileStack> = {
  merge_pdfs: FileStack,
  split_pdf: Scissors,
  reorder_pages: ArrowUpDown,
  protect_pdf: Lock,
  sign_pdf: PenLine,
  image_to_pdf: ImageIcon,
}

const operationLabels: Record<string, string> = {
  merge_pdfs: "Merge",
  split_pdf: "Split",
  reorder_pages: "Reorder",
  protect_pdf: "Protect",
  sign_pdf: "Sign",
  image_to_pdf: "Image to PDF",
}

const statusVariants: Record<
  string,
  "default" | "secondary" | "destructive" | "outline"
> = {
  pending: "secondary",
  processing: "secondary",
  completed: "default",
  failed: "destructive",
}

function formatFileSize(bytes: number | null): string {
  if (!bytes) return "-"
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function DocumentTable({ documents, plan }: DocumentTableProps) {
  const [selected, setSelected] = useState<Set<number>>(new Set())
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const isFreeUser = plan === "free"

  const toggleSelect = (id: number) => {
    const next = new Set(selected)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    setSelected(next)
  }

  const toggleSelectAll = () => {
    if (selected.size === documents.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(documents.map((d) => d.id)))
    }
  }

  const handleDelete = (id: number) => {
    if (!confirm("Are you sure you want to delete this document?")) return

    router.delete(`/documents/${id}`, {
      preserveScroll: true,
      onSuccess: () => {
        const next = new Set(selected)
        next.delete(id)
        setSelected(next)
        toast.success("Document deleted")
      },
    })
  }

  const handleBulkDelete = () => {
    const count = selected.size
    if (
      !confirm(
        `Are you sure you want to delete ${count} document${count > 1 ? "s" : ""}?`,
      )
    )
      return

    router.delete("/documents/destroy_many", {
      data: { ids: Array.from(selected) },
      preserveScroll: true,
      onSuccess: () => {
        setSelected(new Set())
        toast.success(`${count} document${count > 1 ? "s" : ""} deleted`)
      },
    })
  }

  return (
    <div>
      {selected.size > 0 && (
        <div className="bg-muted/50 mb-4 flex items-center justify-between rounded-lg border p-3">
          <span className="text-sm">
            {selected.size} document{selected.size > 1 ? "s" : ""} selected
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelected(new Set())}
            >
              Deselect all
            </Button>
            <Button variant="destructive" size="sm" onClick={handleBulkDelete}>
              Delete selected
            </Button>
          </div>
        </div>
      )}

      <div className="rounded-lg border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={
                    selected.size === documents.length && documents.length > 0
                  }
                  onCheckedChange={toggleSelectAll}
                />
              </TableHead>
              <TableHead>File</TableHead>
              <TableHead>Operation</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Size</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.map((doc) => {
              const Icon = doc.operation_type
                ? operationIcons[doc.operation_type] || FileStack
                : FileStack

              const isExpired = doc.expired
              const canDownload =
                doc.status === "completed" && !isExpired && doc.download_url

              return (
                <TableRow
                  key={doc.id}
                  className={isExpired ? "opacity-50" : ""}
                >
                  <TableCell>
                    <Checkbox
                      checked={selected.has(doc.id)}
                      onCheckedChange={() => toggleSelect(doc.id)}
                    />
                  </TableCell>
                  <TableCell>
                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="block max-w-[200px] truncate">
                            {doc.filename}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent>{doc.filename}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4" />
                      <span>
                        {doc.operation_type
                          ? operationLabels[doc.operation_type] ||
                            doc.operation_type
                          : "-"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        isExpired ? "outline" : statusVariants[doc.status]
                      }
                    >
                      {isExpired ? "Expired" : doc.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {formatDistanceToNow(new Date(doc.created_at), {
                      addSuffix: true,
                    })}
                  </TableCell>
                  <TableCell>{formatFileSize(doc.size)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {canDownload &&
                        (isFreeUser ? (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setShowUpgradeModal(true)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        ) : (
                          <Button variant="ghost" size="icon" asChild>
                            <a href={doc.download_url!} download>
                              <Download className="h-4 w-4" />
                            </a>
                          </Button>
                        ))}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(doc.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showUpgradeModal} onOpenChange={setShowUpgradeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upgrade to Download</DialogTitle>
            <DialogDescription>
              Download your processed documents by upgrading to a Plus or Pro
              plan. Get unlimited downloads and more features.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowUpgradeModal(false)}
            >
              Cancel
            </Button>
            <Button asChild>
              <Link href={pricingPath()}>View Plans</Link>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
